import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type ConsentStatus = "pending" | "accepted" | "declined";

interface CookieConsentContextType {
  consent: ConsentStatus;
  acceptCookies: () => void;
  declineCookies: () => void;
  hasInteracted: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const CONSENT_KEY = "splora_cookie_consent";

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
  const [consent, setConsent] = useState<ConsentStatus>("pending");
  const [hasInteracted, setHasInteracted] = useState(false);

  // Load consent from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === "accepted" || stored === "declined") {
      setConsent(stored);
      setHasInteracted(true);
    }
  }, []);

  const acceptCookies = () => {
    setConsent("accepted");
    setHasInteracted(true);
    localStorage.setItem(CONSENT_KEY, "accepted");
  };

  const declineCookies = () => {
    setConsent("declined");
    setHasInteracted(true);
    localStorage.setItem(CONSENT_KEY, "declined");
  };

  return (
    <CookieConsentContext.Provider value={{ consent, acceptCookies, declineCookies, hasInteracted }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error("useCookieConsent must be used within CookieConsentProvider");
  }
  return context;
};
